<template>
  <el-dialog
    v-model="visible"
    :width="dialogConfig.type == 'edit' ? '30%' : '60%'"
    :title="dialogConfig.type == 'edit' ? '库存预警设置' : '详情'"
    :z-index="1000"
  >
    <el-form
      :model="formData"
      ref="ruleRef"
      :rules="rules"
      v-if="dialogConfig.type == 'edit'"
      v-loading="dialogLoading"
    >
      <el-form-item label="库存上限：" prop="upperLimit">
        <el-input v-model="formData.upperLimit" />
      </el-form-item>
      <el-form-item label="库存下限：" prop="lowerLimit">
        <el-input v-model="formData.lowerLimit" />
      </el-form-item>
    </el-form>

    <div v-else v-loading="dialogLoading">
      <div class="title">物料信息</div>
      <el-descriptions border :column="3">
        <template v-for="item in descriptionColumns" :key="item.value">
          <el-descriptions-item :label="item.label" width="16%">
            <span class="my_class">{{
              item.value === "total"
                ? formData.totalCost
                : formData[item.value]
                ? formData[item.value]
                : "-"
            }}</span></el-descriptions-item
          >
        </template>
      </el-descriptions>

      <div class="title">仓库信息</div>
      <el-table
        :header-cell-style="{ 'text-align': 'center' }"
        :cell-style="{ 'text-align': 'center' }"
        :data="tableData"
      >
        <el-table-column label="序号" type="index" width="60" />
        <template v-for="item in columns" :key="item.prop">
          <el-table-column :label="item.label" :prop="item.prop" />
        </template>

        <el-table-column
          label="表具编号(仅重点物料显示)"
          :prop="keyNumberList"
          width="200"
          v-if="isKey"
        >
          <template #default="{ row }">
            <span
              style="color: #3478f6; cursor: pointer"
              @click="openKeyNumberPop(row)"
              >{{ row.keyNumberList.length + "个编号" }}</span
            >
          </template>
        </el-table-column>
      </el-table>
    </div>

    <template #footer>
      <span class="dialog-footer">
        <el-button @click="handleCancel()">取消</el-button>
        <el-button
          type="primary"
          @click="handleSUbmit()"
          :disabled="loading"
          v-if="dialogConfig.type == 'edit'"
        >
          保存
        </el-button>
      </span>
    </template>
    <!-- 表具编号新增编辑查看组件 -->
    <KeyNumberEdit ref="keyNumberRef" />
  </el-dialog>
</template>

<script setup>
import { ref, reactive } from "vue";
import { warningSetting, queryInventoryById } from "@/api/inventory";
import KeyNumberEdit from "@/components/KeyNumberEdit.vue";
import { ElMessage } from "element-plus";

const emits = defineEmits(["afterSave"]);

const visible = ref(false);

const formData = reactive({
  id: null,
  upperLimit: null,
  lowerLimit: null,
});

const ruleRef = ref(null);

// 保存按钮的loading
const loading = ref(false);

// 弹窗遮罩
const dialogLoading = ref(false);

// 弹窗参数
const dialogConfig = ref({});

const tableData = ref([]);
const isKey = ref(0); // 是否重点物料

const columns = ref([
  {
    label: "仓库名称",
    prop: "warehouseDTO.name",
  },
  {
    label: "数量",
    prop: "quantity",
  },
  {
    label: "单位",
    prop: "unit",
  },
  {
    label: "所属组织",
    prop: "warehouseDTO.orgName",
  },
]);

// 描述列表
const descriptionColumns = ref([
  {
    label: "物料编号",
    value: "number",
  },
  {
    label: "物料名称",
    value: "name",
  },
  {
    label: "物料类别",
    value: "categoryName",
  },
  {
    label: "品牌",
    value: "brand",
  },
  {
    label: "规格型号",
    value: "model",
  },
  {
    label: "库存数量",
    value: "totalQuantity",
  },
  {
    label: "单位",
    value: "unit",
  },
  {
    label: "库存单价",
    value: "realTimeUnitPrice",
  },
  {
    label: "库存总价",
    value: "total",
  },
  {
    label: "库存上限",
    value: "upperLimit",
  },
  {
    label: "库存下限",
    value: "lowerLimit",
  },
]);

// 校验下限不可大于上限
const validateLowerLimit = (rule, value, callback) => {
  if (value * 1 > formData.upperLimit * 1) {
    callback(new Error("库存下限不可大于上限"));
  } else {
    callback();
  }
};

const rules = reactive({
  upperLimit: [
    { required: true, message: "请输入库存上限", trigger: "blur" },
    { pattern: /^\+?[0-9]\d*$/, message: "请输入大于等于0的数字" },
  ],
  lowerLimit: [
    { required: true, message: "请输入库存下限", trigger: "blur" },
    { pattern: /^\+?[0-9]\d*$/, message: "请输入大于等于0的数字" },
    { validator: validateLowerLimit, trigger: "blur" },
  ],
});

const handleSUbmit = () => {
  loading.value = true;
  ruleRef.value?.validate((valid) => {
    if (valid) {
      editLimit(formData);
    } else {
      loading.value = false;
    }
  });
};

// 点击取消
const handleCancel = () => {
  ruleRef.value?.resetFields();
  visible.value = false;
};

// 编辑
const editLimit = (req) => {
  warningSetting(req).then((res) => {
    if (res.code == "2000") {
      ElMessage.success("操作成功");
      visible.value = false;
      emits("afterSave");
    }
  });
  setTimeout(() => {
    loading.value = false;
  }, 300);
};

const handleDialogOpen = (config) => {
  dialogConfig.value = { ...config };
  formData.id = config.row.id;
  getDetailInfo(config.row.id);
  visible.value = true;
};

// 获取详情
const getDetailInfo = (id) => {
  dialogLoading.value = true;
  queryInventoryById(id)
    .then((res) => {
      const keys = [
        "id",
        "number",
        "name",
        "categoryName",
        "brand",
        "model",
        "orgName",
        "upperLimit",
        "totalCost",
        "realTimeUnitPrice",
        "lowerLimit",
        "inventoryNumber",
        "unit",
        "totalQuantity",
      ];
      keys.forEach((key) => {
        formData[key] = res.data[key] ?? null;
      });
      tableData.value = res.data.inventoryDetailDTOList ?? [];
      isKey.value = res.data.key;
    })
    .finally(() => {
      dialogLoading.value = false;
    });
};
// 表具编号相关
const keyNumberRef = ref(null);
const openKeyNumberPop = (row) => {
  keyNumberRef.value?.handleDialogOpen({
    row: {
      materialName: formData.name,
      number: formData.number,
      model: formData.model,
      keyNumberList: row.keyNumberList,
    },
    dialogType: 3,
  });
};
defineExpose({
  handleDialogOpen,
});
</script>

<style scoped>
.title {
  border-left: 4px solid #409eff;
  width: 120px;
  height: 15px;
  line-height: 15px;
  font-size: 14px;
  margin: 15px 0;
}
.my_class {
  max-width: 100%;
  word-break: break-all;
  word-wrap: break-word;
}
</style>
